import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { SearchBar } from '../../Components/SearchBar'
import { ChatResult } from '../../Components/SearchResultItem/ChatResult'
import { DdCaseResult } from '../../Components/SearchResultItem/DdCaseResult'
import { useSearch } from '../../Common/Hooks/useSearch'

const PageWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  box-sizing: border-box;
`

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.font.primary};
  margin: 0 0 1.5rem 0;
`

const SearchBarWrapper = styled.div`
  margin-bottom: 2rem;
`

const Section = styled.section`
  margin-bottom: 2rem;
`

const SectionTitle = styled.h2`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.font.grey2};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.75rem 0;
`

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const StateMessage = styled.p`
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.font.grey2};
  margin: 0;
  text-align: center;
  padding: 3rem 0;
`

const ErrorMessage = styled.p`
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.danger};
  margin: 0;
  text-align: center;
  padding: 2rem 0;
`

const HasMoreNote = styled.p`
  font-size: 0.75rem;
  color: ${(props) => props.theme.colors.font.grey2};
  margin: 0.5rem 0 0 0;
  text-align: right;
`

const MIN_QUERY_LENGTH = 2

export const SearchPage = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const q = searchParams.get('q') ?? ''

  const { search, results, loading, error, clear } = useSearch()

  // Trigger search whenever the URL query param changes
  useEffect(() => {
    if (q.length >= MIN_QUERY_LENGTH) {
      search({ q })
    } else {
      clear()
    }
  }, [q]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = useCallback(
    (value: string) => {
      setSearchParams(value ? { q: value } : {}, { replace: true })
    },
    [setSearchParams]
  )

  const hasChats = (results?.chats.results.length ?? 0) > 0
  const hasDdCases = (results?.ddCases.results.length ?? 0) > 0
  const hasAnyResults = hasChats || hasDdCases

  return (
    <PageWrapper>
      <PageTitle>{t('common.searchPage.title')}</PageTitle>

      <SearchBarWrapper>
        <SearchBar
          placeholder={t('common.searchPage.searchBarPlaceholder')}
          onSearch={handleSearch}
          initialValue={q}
          debounceMs={300}
        />
      </SearchBarWrapper>

      {!q && (
        <StateMessage>{t('common.searchPage.promptMessage')}</StateMessage>
      )}

      {q && q.length < MIN_QUERY_LENGTH && (
        <StateMessage>{t('common.searchPage.minLengthMessage', { count: MIN_QUERY_LENGTH })}</StateMessage>
      )}

      {q && q.length >= MIN_QUERY_LENGTH && loading && (
        <StateMessage>{t('common.searchPage.searchingMessage')}</StateMessage>
      )}

      {error && !loading && (
        <ErrorMessage>{error}</ErrorMessage>
      )}

      {!loading && !error && results && !hasAnyResults && q.length >= MIN_QUERY_LENGTH && (
        <StateMessage>{t('common.searchPage.noResultsMessage', { query: results.query })}</StateMessage>
      )}

      {!loading && results && hasChats && (
        <Section>
          <SectionTitle>
            {t('common.searchPage.chatsSectionTitle', { count: results.chats.results.length, suffix: results.chats.hasMore ? '+' : '' })}
          </SectionTitle>
          <ResultsList>
            {results.chats.results.map((result) => (
              <ChatResult key={result.matchedMessageId} result={result} />
            ))}
          </ResultsList>
          {results.chats.hasMore && (
            <HasMoreNote>{t('common.searchPage.hasMoreNote')}</HasMoreNote>
          )}
        </Section>
      )}

      {!loading && results && hasDdCases && (
        <Section>
          <SectionTitle>
            {t('common.searchPage.ddCasesSectionTitle', { count: results.ddCases.results.length, suffix: results.ddCases.hasMore ? '+' : '' })}
          </SectionTitle>
          <ResultsList>
            {results.ddCases.results.map((result) => (
              <DdCaseResult key={result.caseId} result={result} />
            ))}
          </ResultsList>
          {results.ddCases.hasMore && (
            <HasMoreNote>{t('common.searchPage.hasMoreNote')}</HasMoreNote>
          )}
        </Section>
      )}
    </PageWrapper>
  )
}
